import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getBusinessDetail,
  getBusinessSites,
  assignBusinessSites,
  getBusinessInvoicesAdmin,
  removeBusinessEmployeeAdmin,
  resetBusinessPassword,
  updateBusiness,
  assignBusinessWorker,
  creditBusiness,
  getSites,
  getClientsSimple,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Building2, ArrowLeft, Mail, Phone, Building, Calendar, MapPin,
  Zap, Users, BarChart3, Key, Plus, MoreHorizontal,
  TrendingUp, Battery, Wallet, DollarSign, FileText, UserPlus,
  Trash2, CreditCard, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const BusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSites, setAllSites] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [activeTab, setActiveTab] = useState('info');

  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignSitesDialogOpen, setAssignSitesDialogOpen] = useState(false);
  const [assignWorkerDialogOpen, setAssignWorkerDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  // Form states
  const [editForm, setEditForm] = useState({});
  const [assignWorkerForm, setAssignWorkerForm] = useState({ client_id: '', employee_name: '', role: 'employee', monthly_limit_lek: '' });
  const [creditForm, setCreditForm] = useState({ amount: '', reason: 'admin_credit' });
  const [newPassword, setNewPassword] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [detailRes, allSitesRes, clientsRes] = await Promise.all([
        getBusinessDetail(id),
        getSites(),
        getClientsSimple(),
      ]);
      setData(detailRes.data);
      setAllSites(allSitesRes.data || []);
      const items = clientsRes.data.items || clientsRes.data.clients || clientsRes.data || [];
      setAllClients(Array.isArray(items) ? items : []);

      const assignedIds = detailRes.data.sites?.map(s => s.id) || [];
      setSelectedSites(assignedIds);

      const biz = detailRes.data.business || {};
      setEditForm({
        full_name: biz.full_name || '',
        business_name: biz.business_name || '',
        phone: biz.phone || '',
        nipt: biz.nipt || '',
        address: biz.address || '',
        city: biz.city || '',
        status: biz.status || 'active',
      });
    } catch (error) {
      console.error('Failed to fetch business detail:', error);
      toast.error('Gabim në ngarkimin e biznesit');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const loadInvoices = useCallback(async () => {
    if (invoicesLoaded) return;
    try {
      const res = await getBusinessInvoicesAdmin(id);
      setInvoices(res.data.invoices || []);
      setInvoicesLoaded(true);
    } catch {
      toast.error('Gabim në ngarkimin e faturave');
    }
  }, [id, invoicesLoaded]);

  useEffect(() => {
    if (activeTab === 'invoices') loadInvoices();
  }, [activeTab, loadInvoices]);

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateBusiness(id, editForm);
      toast.success('Biznesi u përditësua');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në përditësim');
    }
  };

  const handleAssignSites = async () => {
    try {
      await assignBusinessSites(id, selectedSites);
      toast.success('Stacionet u caktuan');
      setAssignSitesDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Gabim në caktimin e stacioneve');
    }
  };

  const handleAssignWorker = async (e) => {
    e.preventDefault();
    try {
      await assignBusinessWorker(id, {
        client_id: assignWorkerForm.client_id,
        employee_name: assignWorkerForm.employee_name || undefined,
        role: assignWorkerForm.role,
        monthly_limit_lek: assignWorkerForm.monthly_limit_lek ? parseFloat(assignWorkerForm.monthly_limit_lek) : undefined,
      });
      toast.success('Punonjësi u caktua');
      setAssignWorkerDialogOpen(false);
      setAssignWorkerForm({ client_id: '', employee_name: '', role: 'employee', monthly_limit_lek: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në caktimin e punonjësit');
    }
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    try {
      const res = await creditBusiness({ business_id: id, amount: parseFloat(creditForm.amount), reason: creditForm.reason });
      toast.success(`Biznesi u kreditua me ${creditForm.amount} Kredite. Fatura: ${res.data.invoice_number}`);
      setCreditDialogOpen(false);
      setCreditForm({ amount: '', reason: 'admin_credit' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në kreditimin e biznesit');
    }
  };

  const handleRemoveEmployee = async (employeeRecordId, employeeName) => {
    if (!window.confirm(`Jeni të sigurt që doni të largoni "${employeeName}"?`)) return;
    try {
      await removeBusinessEmployeeAdmin(id, employeeRecordId);
      toast.success('Punonjësi u largua');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) { toast.error('Minimum 6 karaktere'); return; }
    try {
      await resetBusinessPassword(id, newPassword);
      toast.success('Fjalëkalimi u ndryshua');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const toggleSite = (siteId) => {
    setSelectedSites(prev => prev.includes(siteId) ? prev.filter(s => s !== siteId) : [...prev, siteId]);
  };

  const formatCurrency = (n) => `${Number(n || 0).toLocaleString()} Kredite`;

  const getInvoiceLabel = (inv) => {
    const t = inv.type || inv.invoice_type || '';
    if (t === 'cash_topup' || inv.display_type === 'Rimbushje cash') return 'Rimbushje Cash';
    if (t === 'business_credit') return 'Kredim Biznesi';
    if (t === 'business_employee_allocation') return 'Allokim Punonjësi';
    if (t === 'charging') return 'Karikim';
    return inv.display_type || t || 'Faturë';
  };

  const getInvoiceCurrency = (inv) => {
    const mergedType = `${String(inv?.type || '').toLowerCase()} ${String(inv?.invoice_type || '').toLowerCase()}`;
    const isTopupLike =
      mergedType.includes('cash_topup') ||
      mergedType.includes('cash_recharge') ||
      mergedType.includes('online_topup') ||
      mergedType.includes('business_credit') ||
      mergedType.includes('business_employee_allocation');
    return isTopupLike ? 'LEK' : 'Kredite';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-64" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <p className="text-zinc-400">Biznesi nuk u gjet</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin-businesses')}>Kthehu</Button>
      </div>
    );
  }

  const { business, wallet, employees, sites, chargers, stats, recent_transactions } = data;
  const businessMainBalance = Number(wallet?.business_main_balance ?? wallet?.balance ?? 0);
  const chargingBalance = Number(wallet?.charging_balance ?? stats?.charging_balance ?? 0);
  const stationBalance = Number(wallet?.station_balance ?? stats?.station_balance ?? 0);
  const totalBalance = Number(
    wallet?.total_balance ??
    stats?.wallet_balance ??
    (businessMainBalance + chargingBalance + stationBalance)
  );

  return (
    <div className="space-y-6" data-testid="business-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin-businesses')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-violet-500/10 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-violet-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-outfit">
                {business.business_name || business.full_name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-zinc-400">{business.email}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs border ${
                  business.status === 'active'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                }`}>{business.status === 'active' ? 'Aktiv' : business.status}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setResetPasswordDialogOpen(true)}>
            <Key className="w-4 h-4 mr-2" />
            Fjalëkalimi
          </Button>
          <Button variant="outline" onClick={() => setCreditDialogOpen(true)}>
            <CreditCard className="w-4 h-4 mr-2" />
            Kredito
          </Button>
          <Button onClick={() => setEditDialogOpen(true)}>
            Modifiko
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-violet-500" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{formatCurrency(totalBalance)}</p>
                <p className="text-xs text-zinc-400">Balanca totale</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_employees || 0}</p>
                <p className="text-xs text-zinc-400">Punonjës</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Battery className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_energy_kwh?.toFixed(1) || 0}</p>
                <p className="text-xs text-zinc-400">kWh Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_sessions || 0}</p>
                <p className="text-xs text-zinc-400">Sesione</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-400">Portofoli i biznesit</p>
            <p className="text-lg font-semibold text-white mt-1">{formatCurrency(businessMainBalance)}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-400">Portofoli i karikimit</p>
            <p className="text-lg font-semibold text-white mt-1">{formatCurrency(chargingBalance)}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-400">Totali</p>
            <p className="text-lg font-semibold text-emerald-400 mt-1">{formatCurrency(totalBalance)}</p>
          </CardContent>
        </Card>
        {stationBalance > 0 && (
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-400">Portofoli i stacioneve</p>
              <p className="text-lg font-semibold text-white mt-1">{formatCurrency(stationBalance)}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-800/50 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="info" className="data-[state=active]:bg-zinc-700">Info</TabsTrigger>
          <TabsTrigger value="employees" className="data-[state=active]:bg-zinc-700">
            Punonjësit ({employees?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="sites" className="data-[state=active]:bg-zinc-700">
            Stacionet ({sites?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="chargers" className="data-[state=active]:bg-zinc-700">
            Karikuesit ({chargers?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="finance" className="data-[state=active]:bg-zinc-700">Financat</TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-zinc-700">Faturat</TabsTrigger>
        </TabsList>

        {/* INFO TAB */}
        <TabsContent value="info" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader><CardTitle className="text-white">Informacioni i Biznesit</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { icon: Building2, label: 'Emri i Biznesit', value: business.business_name },
                  { icon: Users, label: 'Pronari', value: business.full_name },
                  { icon: Mail, label: 'Email', value: business.email },
                  { icon: Phone, label: 'Telefon', value: business.phone || 'Pa specifikuar' },
                  { icon: Building, label: 'NIPT', value: business.nipt || 'Pa specifikuar' },
                  { icon: MapPin, label: 'Adresë', value: [business.address, business.city].filter(Boolean).join(', ') || 'Pa specifikuar' },
                  { icon: Calendar, label: 'Regjistruar', value: formatDate(business.created_at) },
                  { icon: ShieldCheck, label: 'Statusi', value: business.status === 'active' ? 'Aktiv' : business.status },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                    <Icon className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-zinc-500">{label}</p>
                      <p className="text-sm text-white">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Punonjësit</CardTitle>
                <CardDescription>Punonjësit e lidhur me këtë biznes</CardDescription>
              </div>
              <Button onClick={() => setAssignWorkerDialogOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Shto Punonjës
              </Button>
            </CardHeader>
            <CardContent>
              {(!employees || employees.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka punonjës të lidhur</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Punonjësi</TableHead>
                      <TableHead className="text-zinc-400">Roli</TableHead>
                      <TableHead className="text-zinc-400">Balanca</TableHead>
                      <TableHead className="text-zinc-400">Alokuar</TableHead>
                      <TableHead className="text-zinc-400">Shpenzuar</TableHead>
                      <TableHead className="text-zinc-400">Limit Mujor</TableHead>
                      <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map(emp => (
                      <TableRow key={emp.id} className="border-white/5">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{emp.employee_name}</p>
                            <p className="text-xs text-zinc-500">{emp.employee_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-300 capitalize">{emp.role || 'employee'}</TableCell>
                        <TableCell className="text-emerald-400 font-medium">{formatCurrency(emp.wallet?.balance)}</TableCell>
                        <TableCell className="text-zinc-300">{formatCurrency(emp.wallet?.allocated_total)}</TableCell>
                        <TableCell className="text-zinc-300">{formatCurrency(emp.wallet?.spent)}</TableCell>
                        <TableCell className="text-zinc-300">
                          {emp.monthly_limit_lek ? formatCurrency(emp.monthly_limit_lek) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => handleRemoveEmployee(emp.id, emp.employee_name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SITES TAB */}
        <TabsContent value="sites" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Stacionet e Caktuara</CardTitle>
                <CardDescription>Stacionet që menaxhon ky biznes</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setAssignSitesDialogOpen(true)}>
                <MapPin className="w-4 h-4 mr-2" />
                Cakto Stacione
              </Button>
            </CardHeader>
            <CardContent>
              {(!sites || sites.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka stacione të caktuara</p>
                  <Button variant="outline" className="mt-3" onClick={() => setAssignSitesDialogOpen(true)}>
                    Cakto Stacione
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Emri</TableHead>
                      <TableHead className="text-zinc-400">Adresa</TableHead>
                      <TableHead className="text-zinc-400">Qyteti</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sites.map(site => (
                      <TableRow key={site.id} className="border-white/5">
                        <TableCell className="text-white font-medium">{site.name}</TableCell>
                        <TableCell className="text-zinc-300">{site.address}</TableCell>
                        <TableCell className="text-zinc-300">{site.city}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHARGERS TAB */}
        <TabsContent value="chargers" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Karikuesit</CardTitle>
              <CardDescription>Karikuesit në stacionet e caktuara</CardDescription>
            </CardHeader>
            <CardContent>
              {(!chargers || chargers.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka karikues (caktoni stacione fillimisht)</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">ID</TableHead>
                      <TableHead className="text-zinc-400">Model</TableHead>
                      <TableHead className="text-zinc-400">Fuqia</TableHead>
                      <TableHead className="text-zinc-400">Statusi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chargers.map(charger => (
                      <TableRow key={charger.id} className="border-white/5">
                        <TableCell className="text-white font-mono text-sm">{charger.charger_id}</TableCell>
                        <TableCell className="text-zinc-300">{charger.model || charger.vendor || '—'}</TableCell>
                        <TableCell className="text-zinc-300">{charger.max_power_kw ? `${charger.max_power_kw} kW` : '—'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            charger.is_online ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                          }`}>{charger.is_online ? 'Online' : 'Offline'}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINANCE TAB */}
        <TabsContent value="finance" className="mt-6 space-y-4">
          {/* Wallet summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 mb-1">Totali</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalBalance)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 mb-1">Portofoli i biznesit</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(businessMainBalance)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 mb-1">Portofoli i karikimit</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(chargingBalance)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 mb-1">Total Kredituar</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(wallet?.total_credited)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-white/10">
              <CardContent className="p-5">
                <p className="text-xs text-zinc-500 mb-1">Total Alokuar</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(wallet?.allocated_total)}</p>
              </CardContent>
            </Card>
            {stationBalance > 0 && (
              <Card className="bg-zinc-900/50 border-white/10">
                <CardContent className="p-5">
                  <p className="text-xs text-zinc-500 mb-1">Portofoli i stacioneve</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stationBalance)}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Credit button */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Veprimet e Kreditimit</CardTitle>
                <CardDescription>Historiku i kreditimeve nga admin tek biznesi</CardDescription>
              </div>
              <Button onClick={() => setCreditDialogOpen(true)}>
                <DollarSign className="w-4 h-4 mr-2" />
                Kredito Biznesin
              </Button>
            </CardHeader>
            <CardContent>
              {(!recent_transactions || recent_transactions.length === 0) ? (
                <div className="text-center py-6 text-zinc-500">
                  <DollarSign className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                  <p>Nuk ka veprime kreditimi</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Data</TableHead>
                      <TableHead className="text-zinc-400">Shuma</TableHead>
                      <TableHead className="text-zinc-400">Arsyeja</TableHead>
                      <TableHead className="text-zinc-400">Balanca Pas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent_transactions.map(tx => (
                      <TableRow key={tx.id} className="border-white/5">
                        <TableCell className="text-zinc-300 text-sm">{formatDate(tx.created_at)}</TableCell>
                        <TableCell className="text-emerald-400 font-medium">+{formatCurrency(tx.amount)}</TableCell>
                        <TableCell className="text-zinc-300">{tx.reason || '—'}</TableCell>
                        <TableCell className="text-zinc-300">{formatCurrency(tx.new_balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVOICES TAB */}
        <TabsContent value="invoices" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Faturat</CardTitle>
              <CardDescription>Të gjitha faturat e biznesit dhe punonjësve</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka fatura</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Nr. Faturës</TableHead>
                      <TableHead className="text-zinc-400">Tipi</TableHead>
                      <TableHead className="text-zinc-400">Emri</TableHead>
                      <TableHead className="text-zinc-400">Shuma</TableHead>
                      <TableHead className="text-zinc-400">Data</TableHead>
                      <TableHead className="text-zinc-400">Statusi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(inv => (
                      <TableRow key={inv.id} className="border-white/5">
                        <TableCell className="text-white font-mono text-xs">{inv.number || inv.id?.slice(0, 8)}</TableCell>
                        <TableCell>
                          <span className="px-2 py-0.5 rounded text-xs bg-zinc-700/50 text-zinc-300">
                            {getInvoiceLabel(inv)}
                          </span>
                        </TableCell>
                        <TableCell className="text-zinc-300">{inv.user_name || inv.employee_name || '—'}</TableCell>
                        <TableCell className="text-white font-medium">{formatCurrency(inv.amount || inv.total, getInvoiceCurrency(inv))}</TableCell>
                        <TableCell className="text-zinc-400 text-sm">{formatDate(inv.created_at)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            inv.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-500/10 text-zinc-400'
                          }`}>{inv.status === 'completed' ? 'Paguar' : inv.status}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">Modifiko Biznesin</DialogTitle>
            <DialogDescription>Përditëso profilin e biznesit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Pronari</Label>
                <Input value={editForm.full_name} onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Emri i Biznesit</Label>
                <Input value={editForm.business_name} onChange={e => setEditForm({ ...editForm, business_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>NIPT</Label>
                <Input value={editForm.nipt} onChange={e => setEditForm({ ...editForm, nipt: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Adresë</Label>
                <Input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Qyteti</Label>
                <Input value={editForm.city} onChange={e => setEditForm({ ...editForm, city: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ASSIGN SITES DIALOG */}
      <Dialog open={assignSitesDialogOpen} onOpenChange={setAssignSitesDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Cakto Stacione</DialogTitle>
            <DialogDescription>Zgjidh stacionet që do të menaxhojë ky biznes</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {allSites.map(site => (
              <div
                key={site.id}
                className="flex items-center space-x-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 cursor-pointer"
                onClick={() => toggleSite(site.id)}
              >
                <Checkbox checked={selectedSites.includes(site.id)} onCheckedChange={() => toggleSite(site.id)} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{site.name}</p>
                  <p className="text-xs text-zinc-500">{site.address}, {site.city}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignSitesDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleAssignSites}>Ruaj ({selectedSites.length} stacione)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ASSIGN WORKER DIALOG */}
      <Dialog open={assignWorkerDialogOpen} onOpenChange={setAssignWorkerDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Shto Punonjës</DialogTitle>
            <DialogDescription>Cakto klient ekzistues si punonjës të biznesit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignWorker} className="space-y-3">
            <div className="space-y-2">
              <Label>Klienti</Label>
              <select
                className="w-full h-10 border border-white/10 rounded-md px-3 bg-zinc-800 text-white"
                value={assignWorkerForm.client_id}
                onChange={e => setAssignWorkerForm({ ...assignWorkerForm, client_id: e.target.value })}
                required
              >
                <option value="">Zgjidh klientin...</option>
                {allClients.filter(c => c.client_type !== 'business').map(c => (
                  <option key={c.id} value={c.id}>{c.full_name || c.email} ({c.email})</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Emri në Biznes (opsionale)</Label>
              <Input
                value={assignWorkerForm.employee_name}
                onChange={e => setAssignWorkerForm({ ...assignWorkerForm, employee_name: e.target.value })}
                placeholder="P.sh. Punëtor 1"
              />
            </div>
            <div className="space-y-2">
              <Label>Limit Mujor (Kredite, opsionale)</Label>
              <Input
                type="number"
                value={assignWorkerForm.monthly_limit_lek}
                onChange={e => setAssignWorkerForm({ ...assignWorkerForm, monthly_limit_lek: e.target.value })}
                placeholder="0 = pa limit"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignWorkerDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Shto Punonjësin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREDIT DIALOG */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Kredito Biznesin</DialogTitle>
            <DialogDescription>
              Shto kredi për {business.business_name || business.full_name} · Portofoli i biznesit: {formatCurrency(businessMainBalance)} · Portofoli i karikimit: {formatCurrency(chargingBalance)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCredit} className="space-y-4">
            <div className="space-y-2">
              <Label>Shuma (Kredite)</Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                value={creditForm.amount}
                onChange={e => setCreditForm({ ...creditForm, amount: e.target.value })}
                placeholder="Shuma"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Arsyeja</Label>
              <Input
                value={creditForm.reason}
                onChange={e => setCreditForm({ ...creditForm, reason: e.target.value })}
                placeholder="Arsyeja e kreditimit"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreditDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Kredito</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Ndrysho Fjalëkalimin</DialogTitle>
            <DialogDescription>Vendosni fjalëkalimin e ri për biznesin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fjalëkalimi i Ri</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 6 karaktere"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleResetPassword}>Ndrysho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessDetail;
